
import { useState, useEffect } from 'react';

const ModernFarming = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('modern-farming');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const farmingMethods = [
    {
      title: 'Square Inch Farming',
      subtitle: 'Container & Barrel Farming',
      description: 'Maximize yield in minimal space using innovative container and barrel growing techniques. Perfect for urban farming and small spaces.',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'ri-grid-line',
      features: ['Space Efficient', 'Water Saving', 'Easy Maintenance', 'High Yield']
    },
    {
      title: 'Tower Garden',
      subtitle: 'Vertical Growth Systems',
      description: 'Grow more in less space with vertical farming towers and living walls that maximize production vertically.',
      image: 'https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'ri-building-line',
      features: ['Vertical Growing', 'Space Maximization', 'Easy Harvesting', 'Modern Design']
    },
    {
      title: 'Greenhouse',
      subtitle: 'Climate-Controlled Glass Structure',
      description: 'Traditional glass greenhouse providing optimal growing conditions with precise temperature and humidity control.',
      image: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'ri-home-4-line',
      features: ['Glass Construction', 'Climate Control', 'Year-round Growing', 'Premium Quality']
    },
    {
      title: 'Polyhouse',
      subtitle: 'Plastic Film Protected Cultivation',
      description: 'Cost-effective polyethylene film structure offering protection from weather while maintaining optimal growing conditions.',
      image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      icon: 'ri-building-2-line',
      features: ['Cost Effective', 'Weather Protection', 'Easy Installation', 'Flexible Design']
    },
    // {
    //   title: 'Glasshouse',
    //   subtitle: 'Premium Glass Cultivation',
    //   description: 'High-end glass structure with advanced ventilation and climate control systems for premium crop production.',
    //   image: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    //   icon: 'ri-home-5-line',
    //   features: ['Premium Materials', 'Advanced Systems', 'Maximum Light', 'Professional Grade']
    // }
  ];

  const innovations = [
    {
      title: 'Smart Irrigation',
      description: 'Automated watering systems with moisture sensors',
      icon: 'ri-drop-line',
      color: 'text-blue-500'
    },
    {
      title: 'Nutrient Management',
      description: 'Precise fertilization for optimal plant health',
      icon: 'ri-flask-line',
      color: 'text-green-500'
    },
    {
      title: 'Growth Monitoring',
      description: 'Track plant development with modern tools',
      icon: 'ri-line-chart-line',
      color: 'text-purple-500'
    },
    {
      title: 'Harvest Optimization',
      description: 'Maximize yield through data-driven decisions',
      icon: 'ri-bar-chart-line',
      color: 'text-orange-500'
    }
  ];

  return (
    <section id="modern-farming" className="py-20 bg-gradient-to-b from-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            Modern Micro Farming Innovations
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover cutting-edge farming techniques including container farming, protected cultivation, and advanced growing systems
          </p>
        </div>

        {/* Innovation Features */}
        <div className={`grid grid-cols-1 md:grid-cols-4 gap-6 mb-16 transform transition-all duration-1000 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          {innovations.map((innovation, index) => (
            <div 
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 text-center"
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center ${innovation.color}`}>
                <i className={`${innovation.icon} text-2xl`}></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{innovation.title}</h3>
              <p className="text-gray-600 text-sm">{innovation.description}</p>
            </div>
          ))}
        </div>

        {/* Farming Methods Tabs */}
        <div className={`mb-8 transform transition-all duration-1000 delay-400 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="flex flex-wrap justify-center gap-4">
            {farmingMethods.map((method, index) => (
              <button
                key={index}
                onClick={() => setActiveTab(index)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === index
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                }`}
              >
                {method.title}
              </button>
            ))}
          </div>
        </div>

        {/* Active Method Display */}
        <div className={`bg-white rounded-3xl overflow-hidden shadow-2xl transform transition-all duration-1000 delay-600 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="md:flex">
            <div className="md:w-1/2">
              <img 
                src={farmingMethods[activeTab].image}
                alt={farmingMethods[activeTab].title}
                className="w-full h-64 md:h-full object-cover"
              />
            </div>
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
              <div className={`w-16 h-16 mb-6 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center text-blue-600`}>
                <i className={`${farmingMethods[activeTab].icon} text-2xl`}></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">
                {farmingMethods[activeTab].title}
              </h3>
              <h4 className="text-xl text-blue-600 font-semibold mb-4">
                {farmingMethods[activeTab].subtitle}
              </h4>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                {farmingMethods[activeTab].description}
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {farmingMethods[activeTab].features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-green-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Growth Animation */}
        <div className={`mt-16 text-center transform transition-all duration-1000 delay-800 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-8 md:p-12 text-white">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Advanced Growing Systems
            </h3>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Experience the future of farming with our container systems, protected cultivation, and innovative growing techniques
            </p>
            
            {/* Animated Growth Icons */}
            <div className="flex justify-center space-x-8 mb-8">
              {['ri-seedling-line', 'ri-plant-line', 'ri-leaf-line', 'ri-flower-line'].map((icon, index) => (
                <div 
                  key={index}
                  className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-2xl animate-pulse"
                  style={{ animationDelay: `${index * 0.5}s` }}
                >
                  <i className={icon}></i>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModernFarming;
